/**
 * Folds a value into its search form: lowercased, trimmed and without accents,
 * so a query typed without diacritics still matches translated copy.
 *
 * @param {string} [value]
 * @returns {string}
 */
export const normalizeForSearch = (value = '') => value
  .normalize('NFD')
  .replaceAll(/\p{Diacritic}/gu, '')
  .toLocaleLowerCase()
  .trim();

const matches = (text, query) => normalizeForSearch(text).includes(query);

const showEverything = ({ groups, flatOptions }) => ({
  groups: groups.map(({ options }) => ({
    visible: true, expanded: null, options: options.map(() => true),
  })),
  flatOptions: flatOptions.map(() => true),
});

const withEmptyFlag = (visibility) => ({
  ...visibility,
  empty: visibility.groups.every(({ visible }) => !visible)
    && visibility.flatOptions.every((visible) => !visible),
});

/**
 * Decides what the department/job-title picker shows for a search query. A
 * department whose own name or description matches keeps all its job titles;
 * otherwise only its matching job titles stay, and any group left with a match
 * is opened so the match is visible. A blank query shows everything and leaves
 * each group open or closed as the user left it (`expanded: null`). `empty`
 * tells the picker to show its no-match state instead of an empty list.
 *
 * @param {{groups: {text: string, options: string[]}[], flatOptions: string[]}} catalog
 *   searchable text (name + description) of each group, its options, and the
 *   options rendered outside any group
 * @param {string} rawQuery -- what the user typed
 * @returns {{
 *   groups: {visible: boolean, expanded: boolean | null, options: boolean[]}[],
 *   flatOptions: boolean[],
 *   empty: boolean
 * }}
 */
export const filterDepartmentJobTitles = ({ groups, flatOptions }, rawQuery) => {
  const query = normalizeForSearch(rawQuery);

  return withEmptyFlag(query === '' ? showEverything({ groups, flatOptions }) : {
    groups: groups.map(({ text, options }) => {
      const departmentMatches = matches(text, query);
      const visibleOptions = options.map((option) => departmentMatches || matches(option, query));
      const visible = visibleOptions.some(Boolean);

      return { visible, expanded: visible, options: visibleOptions };
    }),
    flatOptions: flatOptions.map((option) => matches(option, query)),
  });
};
